import { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { ExportButton } from '@/components/atoms/ExportButton/ExportButton';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDate } from '@/util/dateUtils';

import {
  EFFECTIVENESS_AGGREGATION_LABEL_KEYS,
  EFFECTIVENESS_METRIC_KEYS,
  getEffectivenessValue,
  type EffectivenessAggregation,
  type EffectivenessMetricKey,
  type EffectivenessTimeseriesPoint,
} from './effectivenessChartConfig';
import { HumanAgentsEffectivenessChart } from './HumanAgentsEffectivenessChart';
import { useComparisonMode } from '../../hooks/useComparisonMode';
import { useHumanAgentsEffectiveness } from '../../hooks/useHumanAgentsEffectiveness';
import { useHumanAgentsEffectivenessTimeseries } from '../../hooks/useHumanAgentsEffectivenessTimeseries';
import { useHumanAgentsExport } from '../../hooks/useHumanAgentsExport';
import { selectAnalyticsDates } from '../../redux/selectors';
import { formatDurationDelta } from '../../utils/deltaFormatters';
import { formatDateRangeLabel, isSingleDayRange } from '../../utils/formatDateRangeLabel';
import { formatDurationFromSeconds } from '../../utils/formatDuration';
import { getComparisonDates } from '../../utils/getComparisonDates';
import { AbsoluteChangeIndicator } from '../aiAgents/AbsoluteChangeIndicator';
import { AnalyticsEmptyState } from '../aiAgents/AnalyticsEmptyState';
import { getComparisonPeriodKey } from '../aiAgents/PercentChangeIndicator';
import { KpiTile } from '../KpiTile/KpiTile';

const METRIC_KEYS: EffectivenessMetricKey[] = [
  'firstResponse',
  'response',
  'handling',
  'resolution',
];

const AGGREGATIONS: EffectivenessAggregation[] = ['average', 'maximum', 'median', 'minimum'];

const formatEffectivenessDelta = (delta: number): string => formatDurationDelta(delta, 1);

export const HumanAgentsEffectiveness = () => {
  const { t } = useTranslation();
  const { isComparing } = useComparisonMode();
  const { startDate, endDate } = useSelector(selectAnalyticsDates);

  const { isSingleDay, dateRange } = useMemo(
    () => ({
      isSingleDay: isSingleDayRange(startDate, endDate),
      dateRange: formatDateRangeLabel(startDate, endDate),
    }),
    [startDate, endDate]
  );

  const [aggregation, setAggregation] = useState<EffectivenessAggregation>('average');
  const [selectedMetric, setSelectedMetric] = useState<EffectivenessMetricKey>('firstResponse');

  const { data: effectivenessData, comparisonData, isLoading } = useHumanAgentsEffectiveness();
  const { data: timeseriesRows, comparisonData: comparisonTimeseriesRows } =
    useHumanAgentsEffectivenessTimeseries();
  const { exportWidget, isWidgetExportable, isExporting } = useHumanAgentsExport();

  const comparisonLabel = t(getComparisonPeriodKey(startDate, endDate));

  // Drive the chart x-axis from the selected range (and the previous-period
  // range when comparing) so every day renders even without returned data.
  const period = useMemo(() => ({ start: startDate, end: endDate }), [startDate, endDate]);
  const comparisonPeriod = useMemo(() => {
    const { startDate: start, endDate: end } = getComparisonDates(startDate, endDate);
    return { start, end };
  }, [startDate, endDate]);

  const tiles = useMemo(
    () =>
      METRIC_KEYS.map((key) => {
        const current = getEffectivenessValue(effectivenessData, key, aggregation);
        const previous = getEffectivenessValue(comparisonData, key, aggregation);
        return {
          key,
          label: t(EFFECTIVENESS_METRIC_KEYS[key].label),
          tooltip: t(EFFECTIVENESS_METRIC_KEYS[key].tooltip),
          value: formatDurationFromSeconds(current, 1),
          current,
          previous,
          isMetricEmpty: current == null,
        };
      }),
    [aggregation, effectivenessData, comparisonData, t]
  );

  const isEmpty = useMemo(
    () =>
      !isLoading &&
      METRIC_KEYS.every((key) =>
        AGGREGATIONS.every((agg) => getEffectivenessValue(effectivenessData, key, agg) == null)
      ),
    [effectivenessData, isLoading]
  );

  const chartData = useMemo<EffectivenessTimeseriesPoint[]>(() => {
    if (isSingleDay || isEmpty) {
      return [];
    }
    return timeseriesRows.map((row) => ({
      date: formatDate(row.time_bucket, 'yyyy-MM-dd'),
      // Keep null (no samples) as a gap; the chart never fabricates a 0 for an average.
      value: getEffectivenessValue(row, selectedMetric, aggregation),
    }));
  }, [isSingleDay, isEmpty, timeseriesRows, selectedMetric, aggregation]);

  const comparisonChartData = useMemo<EffectivenessTimeseriesPoint[]>(() => {
    if (!isComparing || isSingleDay || isEmpty) {
      return [];
    }
    return comparisonTimeseriesRows.map((row) => ({
      date: formatDate(row.time_bucket, 'yyyy-MM-dd'),
      // Keep null (no samples) as a gap; the chart never fabricates a 0 for an average.
      value: getEffectivenessValue(row, selectedMetric, aggregation),
    }));
  }, [isComparing, isSingleDay, isEmpty, comparisonTimeseriesRows, selectedMetric, aggregation]);

  return (
    <Card className="rounded-lg bg-background text-foreground shadow-none">
      <CardHeader>
        <div className="flex items-center justify-between gap-4">
          <div className="flex flex-col gap-2">
            <CardTitle>{t('analytics.human_agents_effectiveness.title')}</CardTitle>
            <CardDescription>{dateRange}</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Select
              value={aggregation}
              onValueChange={(v) => setAggregation(v as EffectivenessAggregation)}
            >
              <SelectTrigger className="w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {AGGREGATIONS.map((agg) => (
                  <SelectItem key={agg} value={agg}>
                    {t(EFFECTIVENESS_AGGREGATION_LABEL_KEYS[agg])}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isWidgetExportable('human_agents_effectiveness') && (
              <ExportButton
                variant="icon"
                onExport={(format) => exportWidget('human_agents_effectiveness', format)}
                isExporting={isExporting}
              />
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {isLoading ? (
          <div className="grid grid-cols-4 gap-4">
            {METRIC_KEYS.map((key) => (
              <Skeleton key={key} className="h-20 rounded-lg" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-4 gap-4">
            {tiles.map((tile) => (
              <KpiTile
                key={tile.key}
                label={tile.label}
                tooltip={tile.tooltip}
                value={tile.value}
                isSelected={selectedMetric === tile.key}
                onClick={() => setSelectedMetric(tile.key)}
                comparison={
                  isComparing && !tile.isMetricEmpty ? (
                    <AbsoluteChangeIndicator
                      current={tile.current}
                      previous={tile.previous}
                      formatDelta={formatEffectivenessDelta}
                      precision={0}
                      comparisonLabel={comparisonLabel}
                      invertColors
                      className="mt-1"
                    />
                  ) : undefined
                }
              />
            ))}
          </div>
        )}
        {isLoading && !isSingleDay && <Skeleton className="aspect-4/1 w-full rounded-lg" />}
        {!isLoading && isEmpty && (
          <div className="flex items-center justify-center py-6">
            <AnalyticsEmptyState
              title={t('analytics.no_data_available')}
              description={t('analytics.no_data_for_period')}
            />
          </div>
        )}
        {!isLoading && !isEmpty && !isSingleDay && (
          <HumanAgentsEffectivenessChart
            data={chartData}
            comparisonData={isComparing ? comparisonChartData : undefined}
            metric={selectedMetric}
            t={t}
            period={period}
            comparisonPeriod={comparisonPeriod}
          />
        )}
      </CardContent>
    </Card>
  );
};
